// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Tab from "@insite/mobius/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getCookie, setCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PanelsContainer from "@stoneage/App/Components/JettingAppComponents/JettingAppPanelsContainer";
import JettingAppProductCard from "@stoneage/App/Components/JettingAppComponents/JettingAppProductCard";
import Notify from "@stoneage/App/Components/Notify";
import requestSystemList from "@stoneage/App/Handlers/Data/RequestSystemListHandler";
import getJettingAppMenuPanels from "@stoneage/App/Handlers/JettingApp/getJettingAppMenuPanels";
import getJettingAppMenus from "@stoneage/App/Handlers/JettingApp/getJettingAppMenus";
import getSavedCalculator from "@stoneage/App/Handlers/JettingApp/getSavedCalculator";
import jettingAppLoadProduct from "@stoneage/App/Handlers/JettingApp/jettingAppLoadProduct";
import {
    Calculator,
    CalculatorMenuPanelsResult,
    HoseArray,
    JetArray,
    Panel,
} from "@stoneage/App/Services/JettingApp/JettingAppService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import DynamicDropdown, { OptionObject } from "@stoneage/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { TabPresentationProps } from "@stoneage/MobiusOverrides/Tab/Tab";
import TabGroup, { TabGroupPresentationProps } from "@stoneage/MobiusOverrides/TabGroup/TabGroup";
import Tooltip from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { parseBoolean } from "@stoneage/XDL/Utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

export const enum inputTypes {
    numericBox = "NumericBox",
    button = "Button",
    divider = "Divider",
    smallDivider = "SmallDivider",
    image = "Image",
    dropDown = "DropDown",
    radio = "RadioButton",
    slider = "Slider",
    graph = "Graph",
    jetting = "Jetting",
    hoses = "Hoses",
    textBox = "TextBox",
    checkBox = "CheckBox",
    acceptConfig = "AcceptConfiguration",
    htmlContent = "HtmlContent",
    verifySetup = "VerifySetup",
    toggle = "Toggle",
    jettingMode = "JettingMode",
}

export const enum pressureUnits {
    gpm = "GPM",
    lpm = "LPM",
}

export const enum calculatorUnits {
    us = "US",
    metric = "Metric",
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        calculators: state.xJettingAppCalculators.calculators,
        isLoadingCalculators: state.xJettingAppCalculators.isLoading,
        jettingAppUnit: getCookie("JettingAppUnit") || calculatorUnits.us,
        location: getLocation(state),
        isTabletScreen: state.xCommon.isTabletScreen,
        colors: state.xJettingAppCalculators?.graphColors,
        isLoadingSystemList: state.xJettingAppCalculators?.isLoadingSystemList,
        acceptConfigButtonExists: state.xJettingAppCalculators?.acceptConfigButtonExists,
        isLoadingProduct: state.xJettingAppCalculators.isLoadingProduct,
        productsMap: state.xJettingAppCalculators.product,
    };
};

const mapDispatchToProps = {
    getJettingAppMenus,
    getJettingAppMenuPanels,
    getSavedCalculator,
    requestSystemList,
    jettingAppLoadProduct,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface JettingAppViewStyles {
    tabs?: TabGroupPresentationProps;
    tab?: TabPresentationProps;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    calculatorTitle?: TypographyPresentationProps;
    downIcon?: IconProps;
    tooltipLinkWrapper?: InjectableCss;
    tooltipLink?: LinkPresentationProps;
    dropDownWrapper?: InjectableCss;
    viewWrapper?: InjectableCss;
    errorMessage?: InjectableCss;
}

export const jettingAppViewStyles: JettingAppViewStyles = {};
const styles = jettingAppViewStyles;
const savedConfigId = "savedConfigId";
const messages = {
    tool: {
        title: "Custom_JettingApp_ChangeToolModalTitle",
        message: "Custom_JettingApp_ChangeToolModalMessage",
        submitButton: `${translate("Change Tool")}`,
    },
    family: {
        title: "Custom_JettingApp_ChangeFamilyModalTitle",
        message: "Custom_JettingApp_ChangeFamilyModalMessage",
        submitButton: `${translate("Change Family")}`,
    },
    model: {
        title: "Custom_JettingApp_ChangeModelModalTitle",
        message: "Custom_JettingApp_ChangeModelModalMessage",
        submitButton: `${translate("Change Model")}`,
    },
};
const JettingAppView = ({
    getJettingAppMenuPanels,
    getJettingAppMenus,
    getSavedCalculator,
    calculators,
    isLoadingCalculators,
    jettingAppUnit,
    location,
    isTabletScreen,
    colors,
    isLoadingSystemList,
    requestSystemList,
    history,
    acceptConfigButtonExists,
    productsMap,
    isLoadingProduct,
    jettingAppLoadProduct,
}: Props) => {
    const dispatch = useDispatch();
    const [panels, setPanels] = useState<Panel[]>([]);
    const [isLoadingPanels, setIsLoadingPanels] = useState(false);
    const [selectedCalculator, setSelectedCalculator] = useState<Calculator | null>(null);
    const [selectedParentCalculator, setSelectedParentCalculator] = useState<Calculator | null>(null);
    const [selectedUnit, setSelectedUnit] = useState(jettingAppUnit);
    const [selectedPressureUnit, setSelectedPressureUnit] = useState(pressureUnits.gpm);
    const [onlyCalculateOnButton, setOnlyCalculateOnButton] = useState(false);
    const [initialize, setInitialize] = useState(true);
    const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [acceptConfigButtonDisabled, setIsAcceptConfigButtonDisabled] = useState(false);
    const [productNumber, setProductNumber] = useState("");
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [tempControlValues, setTempControlValues] = useState<{
        value: string | number;
        sectionIndex: number;
        panelIndex: number;
        controlIndex: number;
    } | null>(null);
    const [modalInfo, setModalInfo] = useState<{
        title: string;
        message: string;
        submitButton: string;
    } | null>(null);
    let tippyElement: undefined;

    // Memoize the data
    const memoizedPanels = useMemo(() => panels, [panels]);

    // SAINC-1632 point1
    const panelsRef = useRef<any>(null);
    panelsRef.current = panels;

    useEffect(() => {
        // Call getabs api
        if (!calculators?.length && !isLoadingCalculators) {
            getJettingAppMenus({
                onSuccess(result) {
                    setDisplayErrorMessage(false);
                    setIsLoadingPanels(true);
                    // Check if we have calculator id in the url
                    const selectedCalcId = getQuery("calculatorId");
                    let selectedCalculator;

                    if (selectedCalcId && result.calculators) {
                        for (const calc of result.calculators as Calculator[]) {
                            if (calc.calculatorId === selectedCalcId) {
                                selectedCalculator = calc;
                                setSelectedCalculator(selectedCalculator);
                                setSelectedParentCalculator(selectedCalculator);
                                break;
                            } else if (calc.children?.length) {
                                const selected = calc.children.find(c => c.calculatorId === selectedCalcId);
                                if (selected) {
                                    selectedCalculator = selected;
                                    setSelectedCalculator(selectedCalculator);
                                    setSelectedParentCalculator(calc);
                                    break;
                                }
                            }
                        }
                    } else {
                        const firstCalculator = result?.calculators?.[0] as Calculator;
                        if (firstCalculator?.children?.length) {
                            setSelectedCalculator(firstCalculator.children[0]);
                            selectedCalculator = firstCalculator.children[0];
                        } else {
                            selectedCalculator = firstCalculator;
                            setSelectedCalculator(firstCalculator);
                        }
                        setSelectedParentCalculator(firstCalculator);
                    }
                    if (selectedCalculator) {
                        // Before calling getJettingPanels, we need to check if savedConfigId, then either call this api or get saved api
                        const savedId = getQuery(savedConfigId);
                        if (savedId) {
                            // Call get saved config...
                            getSavedCalculator({
                                savedConfigId: savedId,
                                onSuccess(result) {
                                    setPanels(result.panels);
                                    setOnlyCalculateOnButton(result.onlyCalculateOnButton);
                                    setInitialize(result.initialize);
                                    result.units && setSelectedUnit(result.units);
                                    setCookie("JettingAppUnit", result.units);
                                    localStorage.setItem("jettingAppInitialize", result.initialize.toString());
                                    setIsLoadingPanels(false);
                                },
                                onError() {
                                    setIsLoadingPanels(false);
                                },
                            });
                        } else {
                            getJettingPanels(selectedCalculator?.calculatorId, false, true);
                        }
                    }
                },
                onError() {
                    setDisplayErrorMessage(true);
                },
            });
        }
        // handle case when we leave jettingCalculator page and visit it again
        else {
            // here we need to call get panels and check if we have already selected calculator then call it else call first tab
            setIsLoadingPanels(true);
            // Check if we have calculator id in the url
            const selectedCalcId = getQuery("calculatorId");
            let selectedCalculator;

            if (selectedCalcId && calculators) {
                for (const calc of calculators as Calculator[]) {
                    if (calc.calculatorId === selectedCalcId) {
                        selectedCalculator = calc;
                        setSelectedCalculator(selectedCalculator);
                        setSelectedParentCalculator(selectedCalculator);
                        break;
                    } else if (calc.children?.length) {
                        const selected = calc.children.find(c => c.calculatorId === selectedCalcId);
                        if (selected) {
                            selectedCalculator = selected;
                            setSelectedCalculator(selectedCalculator);
                            setSelectedParentCalculator(calc);
                            break;
                        }
                    }
                }
            } else {
                const firstCalculator = calculators?.[0] as Calculator;
                if (firstCalculator?.children?.length) {
                    setSelectedCalculator(firstCalculator.children[0]);
                    selectedCalculator = firstCalculator.children[0];
                } else {
                    selectedCalculator = firstCalculator;
                    setSelectedCalculator(firstCalculator);
                }
                setSelectedParentCalculator(firstCalculator);
            }
            if (selectedCalculator) {
                getJettingPanels(selectedCalculator?.calculatorId, false, true);
            }
        }
    }, []);

    useEffect(() => {
        if (acceptConfigButtonExists) {
            const productPartNumber = panels?.[0]?.sections?.[0]?.controls?.[1]?.value || "";
            if (productPartNumber !== productNumber) {
                setProductNumber(productPartNumber.toString());
            }
        }
    }, [panels, acceptConfigButtonExists]);

    useEffect(() => {
        if (!getCookie("JettingAppUnit")) {
            setCookie("JettingAppUnit", "US");
        }

        // Get graphs colors from systemList
        if (!colors?.length && !isLoadingSystemList) {
            dispatch({
                type: "xJettingAppCalculators/SetIsLoadingSystemList",
            } as any);
            requestSystemList({
                slug: "JettingCalculatorGraphColors",
                onComplete(resultProps) {
                    if (resultProps.apiResult?.successful) {
                        dispatch({
                            type: "xJettingAppCalculators/SetGraphColors",
                            colors: resultProps.apiResult?.result?.values as { name: string; description: string }[],
                        } as any);
                    }
                },
            });
        }
    }, []);

    useEffect(() => {
        const options: OptionObject[] = [];
        calculators.forEach(calc => {
            if (calc.children?.length) {
                options.push({ optionText: calc.title, optionValue: calc.calculatorId, disabled: true });
                calc.children.forEach(child => {
                    options.push({ optionText: child.title, optionValue: child.calculatorId });
                });
            } else {
                options.push({ optionText: calc.title, optionValue: calc.calculatorId });
            }
        });
        setOptions(options);
    }, [calculators]);

    useEffect(() => {
        dispatch({
            type: "xJettingAppCalculators/ClearStoredConfigs",
        } as any);
    }, [selectedCalculator]);

    useEffect(() => {
        const product = productsMap?.[productNumber];
        if (productNumber && !product && !isLoadingProduct) {
            jettingAppLoadProduct({ productNumbers: [productNumber] });
        }
    }, [productNumber]);

    const addCalculatorIdToUrl = (url: string, calculatorId: string) => {
        const urlObj = new URL(url);
        const currentParams = urlObj.searchParams;

        // Create a new URL that has calculatorId
        const newParams = new URLSearchParams();
        newParams.set("calculatorId", calculatorId);
        // after changing selected calculator, we need to remove savedConfigId from url
        if (currentParams.has(savedConfigId)) {
            currentParams.delete(savedConfigId);
        }

        for (const [key, value] of currentParams) {
            if (key !== "calculatorId") {
                newParams.append(key, value);
            }
        }
        urlObj.search = newParams.toString();
        return urlObj.toString();
    };

    const updateUrl = (id: string) => {
        const currentUrl = window.location.href;
        const newUrl = addCalculatorIdToUrl(currentUrl, id);
        window.history.replaceState({}, document.title, newUrl);
    };

    const removeParameterFromUrl = (param: string) => {
        const currentUrl = window.location.href;
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.has(param)) {
            queryParams.delete(param); // Remove the parameter
            const urlObj = new URL(currentUrl);
            urlObj.search = queryParams.toString();
            const newUrl = urlObj.toString();
            window.history.replaceState({}, document.title, newUrl);
        }
    };

    const getQuery = (queryName: string) => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get(queryName);
    };

    const getJettingPanels = (id: string, initial?: boolean, isFirstCall?: boolean) => {
        const selectedValue = isFirstCall ? parseUrlParams() : {};
        getJettingAppMenuPanels({
            id,
            parameter: {
                changeUnits: false,
                initialize: initial || initialize,
                units: selectedUnit,
                onlyCalculateOnButton: initial || false,
                selectedValue,
            },
            onSuccess(result) {
                setOnlyCalculateOnButton(result.onlyCalculateOnButton);
                setInitialize(result.initialize);
                localStorage.setItem("jettingAppInitialize", result.initialize.toString());
                dispatch({
                    type: "xJettingAppCalculators/HideOtherPanels",
                    hideOtherPanels: true,
                } as any);
                setPanels(result.panels);
                setIsLoadingPanels(false);
                if (result.panels) {
                    checkForErrors(result.panels);
                    checkForAcceptConfigAndVerifySetupButton(result.panels);
                }
                isFirstCall && cleanUrl();
            },
            onError() {
                setIsLoadingPanels(false);
                setPanels([]);
            },
        });
    };

    const checkForAcceptConfigAndVerifySetupButton = (panels: Panel[]) => {
        let acceptConfigControl;
        let verifySetupControl;
        // eslint-disable-next-line no-labels
        outerLoop: for (const panel of panels) {
            for (const section of panel.sections) {
                for (const control of section.controls) {
                    if (control.additionalInfo?.["ControlType"] === inputTypes.acceptConfig) {
                        acceptConfigControl = control;
                    } else if (control.additionalInfo?.["ControlType"] === inputTypes.verifySetup) {
                        verifySetupControl = control;
                    }
                    if (acceptConfigControl && verifySetupControl) {
                        // eslint-disable-next-line no-labels
                        break outerLoop;
                    }
                }
            }
        }

        // store acceptConfigControl to use it later
        if (acceptConfigControl) {
            dispatch({
                type: "xJettingAppCalculators/UpdateAcceptConfigButtonExists",
                acceptConfigButtonExists: true,
                acceptConfigControl,
            } as any);
        }
        if (verifySetupControl) {
            dispatch({
                type: "xJettingAppCalculators/hasVerifySetupButton",
                hasVerifySetupButton: true,
                verifySetupControl,
            } as any);
        }
    };

    const parseUrlParams = () => {
        const url = window.location.href;
        const params = new URL(url).searchParams;
        const selectedValue: Record<string, string> = {};
        const excludedKeys = new Set(["calculatorId", "savedConfigId"]);

        params.forEach((value, key) => {
            if (!excludedKeys.has(key)) {
                if (key.startsWith("config")) {
                    const index = key.replace("config", "");
                    selectedValue[index] = value;
                } else {
                    selectedValue[key] = value;
                }
            }
        });

        return selectedValue;
    };

    const cleanUrl = () => {
        const url = window.location.href;
        const urlObj = new URL(url);
        const calculatorId = urlObj.searchParams.get("calculatorId");

        // Rebuild URL with only calculatorId
        urlObj.search = calculatorId ? `calculatorId=${calculatorId}` : "";
        const newUrl = urlObj.toString();
        window.history.replaceState({}, document.title, newUrl);
    };

    const updateJettingPanels = (
        id: string,
        changeUnit?: boolean,
        newUnit?: string,
        newPanels?: Panel[],
        reset?: boolean,
    ) => {
        // Remove savedConfigId if it exists in the url
        removeParameterFromUrl(savedConfigId);

        const initialize = parseBoolean(localStorage.getItem("jettingAppInitialize") || "false");

        // If new unit is US and pressureunit is lpm or if new unit is metric and pressure unit is gpm, we need to send convertFlow: true
        if (
            changeUnit &&
            // new unit is US
            ((selectedUnit === "Metric" && selectedPressureUnit === pressureUnits.lpm) ||
                // new unit is Metric
                (selectedUnit === "US" && selectedPressureUnit === pressureUnits.gpm))
        ) {
            let found = false;
            for (const panel of newPanels || panels || []) {
                for (const section of panel.sections) {
                    if (section.displayFlowConverter) {
                        section.convertFlowUnits = true;
                        selectedPressureUnit === pressureUnits.gpm
                            ? setSelectedPressureUnit(pressureUnits.lpm)
                            : setSelectedPressureUnit(pressureUnits.gpm);
                        found = true;
                        break;
                    }
                }
                if (found) {
                    break;
                }
            }
        }

        // When unit is changed we only send changeUnit:true
        const parameter = {
            changeUnits: changeUnit,
            initialize: reset || initialize,
            units: newUnit || selectedUnit,
            panels: newPanels || panels,
            onlyCalculateOnButton,
        } as any;

        getJettingAppMenuPanels({
            id,
            parameter,
            onComplete() {
                setIsLoadingPanels(false);
            },
            onSuccess(result) {
                panelsRef.current = result?.panels;
                setPanels(result?.panels);
                setOnlyCalculateOnButton(result.onlyCalculateOnButton);
                setInitialize(result.initialize);
                result.units && setSelectedUnit(result.units);
                jettingAppUnit !== result.units && setCookie("JettingAppUnit", result.units);
                localStorage.setItem("jettingAppInitialize", result.initialize.toString());

                // check if a control has error message, and disable Accept config button if so.
                if (result.panels) {
                    checkForErrors(result.panels);
                    checkForAcceptConfigAndVerifySetupButton(result.panels);
                }
            },
        });
    };

    const checkForErrors = (panels: Panel[]) => {
        const errorExists = panels?.some(panel =>
            panel.sections?.some(section => section?.controls?.some(control => control.message?.type === "Error")),
        );
        if (errorExists) {
            setIsAcceptConfigButtonDisabled(true);
        } else {
            setIsAcceptConfigButtonDisabled(false);
        }
    };

    const closePopover = () => {
        (tippyElement as any)?.hide();
    };

    const openCalculater = (parentCalculator: Calculator, childCalculatorId: string) => {
        const calc = parentCalculator.children.find(calculator => calculator.calculatorId === childCalculatorId);

        if (calc?.title) {
            setSelectedCalculator(calc);
            setSelectedParentCalculator(parentCalculator);
        }
        setIsLoadingPanels(true);
        setInitialize(true);
        localStorage.setItem("jettingAppInitialize", "true");
        getJettingPanels(childCalculatorId, true);
        dispatch({
            type: "xJettingAppCalculators/UpdateCalculatorId",
            calculatorId: childCalculatorId,
        } as any);
        updateUrl(childCalculatorId);
        closePopover();
    };

    const toggleConfirmationModal = (type?: string, label?: string) => {
        setIsConfirmationModalOpen(!isConfirmationModalOpen);
        if (type) {
            const messageByType = messages[type.toLowerCase() as keyof typeof messages] || ({} as any);

            setModalInfo({
                ...messageByType,
                submitButton: label ? `${translate("Change {0}", label)}` : messageByType.submitButton,
            });
        }
    };

    /**
     * Handles changes to input controls within a panel.
     *
     * - Updates the value of the control that triggered the change.
     * - Resets the values of all subsequent controls in the same section to an empty string.
     * - If the control type is "Tool" and already has a value, displays a confirmation modal.
     * - Updates the panels state and triggers API calls if necessary:
     *   - Calls `updateJettingPanels` to fetch updated panel data if `onlyCalculateOnButton` is false.
     *   - Resets the "Suggest Jetting" state if applicable.
     * - Sets the `initialize` state to true if the control type is "Tool", "Family", or "Model".
     *
     * @param value - The new value for the control.
     * @param panelIndex - The index of the panel containing the control.
     * @param sectionIndex - The index of the section containing the control.
     * @param controlIndex - The index of the control being changed.
     */
    const handleInputChange = (
        value: string | number,
        panelIndex: number,
        sectionIndex: number,
        controlIndex: number,
    ) => {
        const newPanels = structuredClone(panelsRef.current);
        // check if input is family/tool/model drop down, then display a confirmation modal
        const ctrl = newPanels[panelIndex].sections[sectionIndex].controls[controlIndex];
        if (["Tool", "Family", "Model"].includes(ctrl.additionalInfo?.["ControlType"] || "") && ctrl.value) {
            toggleConfirmationModal(ctrl.additionalInfo?.["ControlType"], ctrl?.label);
            setTempControlValues({ value, panelIndex, sectionIndex, controlIndex });
            return;
        }

        newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].value = value;

        // check if we need to reset jetting
        // if this control panel index >= jetting toggle panel index then uncheck
        let updatedPanels = newPanels;
        if (ctrl.additionalInfo?.["ControlType"] !== inputTypes.jettingMode) {
            updatedPanels = updateSuggestJetting(newPanels, panelIndex, ctrl.resetSuggestJets);
        }

        panelsRef.current = updatedPanels;
        setPanels(updatedPanels);

        let initializeValue = false;
        if (["Tool", "Family", "Model"].includes(ctrl.additionalInfo?.["ControlType"] || "")) {
            setInitialize(true);
            localStorage.setItem("jettingAppInitialize", "true");
            initializeValue = true;

            // Resets the values of subsequent controls only if ("Model" or "Tool") based on changes to "Tool" or "Family".
            for (let i = controlIndex + 1; i < newPanels[panelIndex].sections[sectionIndex].controls.length; i++) {
                const subsequentControl = newPanels[panelIndex].sections[sectionIndex].controls[i];

                if (
                    ctrl.additionalInfo?.["ControlType"] === "Tool" &&
                    subsequentControl.additionalInfo?.["ControlType"] === "Model"
                ) {
                    subsequentControl.value = "";
                }

                if (
                    ctrl.additionalInfo?.["ControlType"] === "Family" &&
                    (subsequentControl.additionalInfo?.["ControlType"] === "Model" ||
                        subsequentControl.additionalInfo?.["ControlType"] === "Tool")
                ) {
                    subsequentControl.value = "";
                }
            }
        }
        // Call api to get result, if calculateOnButton is false
        if (!onlyCalculateOnButton) {
            updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, updatedPanels, initializeValue);
        }
    };

    const handleConfirmationModalCancel = () => {
        setIsConfirmationModalOpen(false);
        setTempControlValues(null);
    };

    const handleConfirmationModalSubmit = () => {
        if (!tempControlValues) {
            setIsConfirmationModalOpen(false);
            return;
        }
        const newPanels = structuredClone(panels);
        const { panelIndex, sectionIndex, controlIndex, value } = tempControlValues;

        // Update the value of the control that triggered the modal
        newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].value = value;

        // Resets the values of subsequent controls only if ("Model" or "Tool") based on changes to "Tool" or "Family".
        for (let i = controlIndex + 1; i < newPanels[panelIndex].sections[sectionIndex].controls.length; i++) {
            const subsequentControl = newPanels[panelIndex].sections[sectionIndex].controls[i];

            if (
                newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].additionalInfo?.["ControlType"] ===
                    "Tool" &&
                subsequentControl.additionalInfo?.["ControlType"] === "Model"
            ) {
                subsequentControl.value = "";
            }

            if (
                newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].additionalInfo?.["ControlType"] ===
                    "Family" &&
                (subsequentControl.additionalInfo?.["ControlType"] === "Model" ||
                    subsequentControl.additionalInfo?.["ControlType"] === "Tool")
            ) {
                subsequentControl.value = "";
            }
        }
        // check if we need to reset jetting
        // if this control panel index >= jetting toggle panel index then uncheck
        const updatedPanels = updateSuggestJetting(
            newPanels,
            panelIndex,
            newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].resetSuggestJets,
        );

        panelsRef.current = updatedPanels;
        setPanels(updatedPanels);

        setInitialize(true);
        localStorage.setItem("jettingAppInitialize", "true");

        // Call api to get result, if calculateOnButton is false
        if (!onlyCalculateOnButton) {
            updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, updatedPanels, true);
        }
        setIsConfirmationModalOpen(false);
    };

    const updateSuggestJetting = (panels: Panel[], panelIndex: number, resetSuggestJets: boolean) => {
        let suggestControl;
        // eslint-disable-next-line no-labels
        outerLoop: for (let pIndex = 0; pIndex < panels.length; pIndex++) {
            for (let secIndex = 0; secIndex < panels[pIndex].sections.length; secIndex++) {
                for (let ctrlIndex = 0; ctrlIndex < panels[pIndex].sections[secIndex].controls.length; ctrlIndex++) {
                    if (
                        panels[pIndex].sections[secIndex].controls[ctrlIndex].additionalInfo?.["ControlType"] ===
                        inputTypes.jettingMode
                    ) {
                        suggestControl = panels[pIndex].sections[secIndex].controls[ctrlIndex];
                        if (panelIndex >= pIndex) {
                            panels[pIndex].sections[secIndex].controls[ctrlIndex].value = false;
                        }
                        // eslint-disable-next-line no-labels
                        break outerLoop;
                    }
                }
            }
        }
        if (resetSuggestJets && suggestControl) {
            suggestControl.value = true;
        }
        return panels;
    };

    const handleButtonClick = (additionalInfo: { [key: string]: string }) => {
        if (additionalInfo?.["ControlType"] === inputTypes.acceptConfig && history) {
            // we need to loop over all controls and jets and build url by adding configPdpConfigId=value
            // need to check if we have variant controlType to add option=ProductNumber to the url
            const pdpUrl = buildPdpUrl();
            pdpUrl && history.push(pdpUrl);
        } else if (additionalInfo?.["ControlType"] === inputTypes.verifySetup) {
            // need to display other panels
            dispatch({
                type: "xJettingAppCalculators/HideOtherPanels",
                hideOtherPanels: false,
            } as any);
        } else {
            updateJettingPanels(selectedCalculator!.calculatorId);
        }
    };

    const buildPdpUrl = () => {
        const productPartNumber = panels?.[0]?.sections?.[0]?.controls?.[1]?.value;
        if (!productPartNumber) {
            return "";
        }
        const product = productsMap?.[productPartNumber.toString() || ""];
        let url = product ? `${product.canonicalUrl}?` : `/Product/${productPartNumber}?`;

        panels.forEach(panel => {
            panel.sections.forEach(section => {
                section.controls.forEach(control => {
                    // option=ProductNumber
                    if (control.additionalInfo?.["ControlType"] === "Variant") {
                        url += `option=${control.value}&`;
                    }
                    if (control.pdpConfigurationId && control.value) {
                        url += `config${control.pdpConfigurationId}=${control.value}&`;
                    }
                    if (control.type === inputTypes.jetting) {
                        control?.jetArray?.forEach(jet => {
                            if (jet.pdpConfigurationId && jet.jetPart) {
                                url += `config${jet.pdpConfigurationId}=${jet.jetPart}&`;
                            }
                        });
                    }
                });
            });
        });
        localStorage.setItem("jettingAppAcceptingConfiguration", "true");
        return url;
    };

    const getPanels = (calculator: Calculator) => {
        setSelectedCalculator(calculator);
        setSelectedParentCalculator(calculator);
        setIsLoadingPanels(true);
        getJettingPanels(calculator.calculatorId, true);
    };

    const changeUnit = (unit: string) => {
        setCookie("JettingAppUnit", unit);
        // on unit change we only send the flag changeUnits: true with the old unit and the api handles the change
        updateJettingPanels(selectedCalculator!.calculatorId, true, selectedUnit);
    };

    const changePressureUnit = (panelIndex: number, sectionIndex: number) => {
        const newPanels = structuredClone(panels);
        newPanels[panelIndex].sections[sectionIndex].convertFlowUnits = true;
        // check if we need to reset jetting
        // if this control panel index >= jetting toggle panel index then uncheck
        const updatedPanels = updateSuggestJetting(newPanels, panelIndex, false);
        panelsRef.current = updatedPanels;
        setPanels(updatedPanels);

        selectedPressureUnit === pressureUnits.gpm
            ? setSelectedPressureUnit(pressureUnits.lpm)
            : setSelectedPressureUnit(pressureUnits.gpm);
        // Call api to get result, if calculateOnButton is false
        if (!onlyCalculateOnButton) {
            updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, updatedPanels, false);
        }
    };

    const onTabClick = (selectedCalc: Calculator) => {
        // If calculator has children then we don't want to call panels api or update url
        if (!selectedCalc.children?.length) {
            getCalcInfoAndUpdateUrl(selectedCalc);
        }
    };

    const selectCalculatorHandler = (calcId?: string) => {
        if (calculators?.length && calcId) {
            let selectedCalc;
            for (const calc of calculators) {
                if (calc.calculatorId === calcId) {
                    selectedCalc = calc;
                    break;
                } else if (calc.children?.length) {
                    for (const child of calc.children) {
                        if (child.calculatorId === calcId) {
                            selectedCalc = child;
                            break;
                        }
                    }
                }
            }
            if (selectedCalc) {
                getCalcInfoAndUpdateUrl(selectedCalc);
            }
        }
    };

    const getCalcInfoAndUpdateUrl = (calc: Calculator) => {
        getPanels(calc);
        dispatch({
            type: "xJettingAppCalculators/UpdateCalculatorId",
            calculatorId: calc.calculatorId,
        } as any);
        updateUrl(calc.calculatorId);
    };

    const handleHosesChange = (
        updatedHoseArray: HoseArray[],
        panelIndex: number,
        sectionIndex: number,
        controlIndex: number,
    ) => {
        const newPanels = structuredClone(panels);
        newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].hoseArray = updatedHoseArray;

        // check if we need to reset jetting
        // if this control panel index >= jetting toggle panel index then uncheck
        const updatedPanels = updateSuggestJetting(
            newPanels,
            panelIndex,
            newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].resetSuggestJets,
        );

        panelsRef.current = updatedPanels;
        setPanels(updatedPanels);

        // Call api to get result, if calculateOnButton is false
        if (!onlyCalculateOnButton) {
            updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, updatedPanels, false);
        }
    };

    const handleJetChange = (
        updatedJetArray: JetArray[],
        panelIndex: number,
        sectionIndex: number,
        controlIndex: number,
    ) => {
        const newPanels = structuredClone(panels);
        newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].jetArray = updatedJetArray;
        // check if we need to reset jetting
        // if this control panel index >= jetting toggle panel index then uncheck
        const updatedPanels = updateSuggestJetting(
            newPanels,
            panelIndex,
            newPanels[panelIndex].sections[sectionIndex].controls[controlIndex].resetSuggestJets,
        );

        panelsRef.current = updatedPanels;
        setPanels(updatedPanels);

        // Call api to get result, if calculateOnButton is false
        if (!onlyCalculateOnButton) {
            updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, updatedPanels, false);
        }
    };

    const downIcon = <StyledIcon src={ChevronDown} {...styles.downIcon} />;

    if (!calculators?.length && !isLoadingCalculators) {
        return (
            <Notify {...styles.errorMessage} messageType="warning">
                {siteMessage("Custom_JettingApp_NoCalculators")}
            </Notify>
        );
    }

    const handleResetAll = () => {
        // set initialize: true and call update api
        updateJettingPanels(selectedCalculator!.calculatorId, false, selectedUnit, panels, true);
        // reset pressure unit to default
        setSelectedPressureUnit(pressureUnits.gpm);
    };

    return (
        <LoadingOverlay loading={isLoadingCalculators}>
            <StyledWrapper {...styles.viewWrapper}>
                <>
                    {isTabletScreen ? (
                        <>
                            <StyledWrapper {...styles.dropDownWrapper}>
                                <DynamicDropdown
                                    label=""
                                    onSelectionChange={selectCalculatorHandler}
                                    selected={selectedCalculator?.calculatorId || calculators[0]?.calculatorId}
                                    options={options}
                                    selectOnly={true}
                                />
                            </StyledWrapper>
                            {acceptConfigButtonExists && <JettingAppProductCard productPartNumber={productNumber} />}
                            <PanelsContainer
                                tab={selectedCalculator || calculators[0]}
                                panels={memoizedPanels}
                                selectedCalculator={selectedCalculator}
                                handleInputChange={handleInputChange}
                                handleButtonClick={handleButtonClick}
                                changePressureUnit={changePressureUnit}
                                selectedUnit={selectedUnit}
                                changeUnit={changeUnit}
                                isLoadingPanels={isLoadingPanels}
                                handleHosesChange={handleHosesChange}
                                handleJetChange={handleJetChange}
                                pressureUnit={selectedPressureUnit}
                                acceptConfigButtonDisabled={acceptConfigButtonDisabled}
                                handleResetAll={handleResetAll}
                                onlyCalculateOnButton={onlyCalculateOnButton}
                            />
                        </>
                    ) : (
                        <TabGroup {...styles.tabs} current={selectedParentCalculator?.title || calculators[0]?.title}>
                            {calculators.map(tab => {
                                const headComponent = (
                                    <Typography>
                                        {tab.title} {downIcon}
                                    </Typography>
                                );
                                let headTooltip = headComponent;
                                if (tab.children) {
                                    headTooltip = (
                                        <Tooltip
                                            trigger="click"
                                            placement="bottom-end"
                                            hideOnClick={true}
                                            onOpen={({ instance }: any) => {
                                                tippyElement = instance;
                                            }}
                                            onClose={() => {
                                                tippyElement = undefined;
                                            }}
                                            delay={[50, 70]}
                                            offset={[13, 12]}
                                            cssOverrides={{
                                                tooltipBody: css``,
                                                tooltipContainer: css`
                                                    > .tippy-arrow {
                                                        display: none;
                                                    }
                                                `,
                                            }}
                                            passAsChildren={true}
                                            triggerComponent={headComponent}
                                        >
                                            <StyledWrapper className="mega-menu-grid-container" as="ul">
                                                {tab.children!.map((child, index) => (
                                                    <StyledWrapper
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={index}
                                                        as="li"
                                                        {...styles.tooltipLinkWrapper}
                                                    >
                                                        <Link
                                                            {...styles.tooltipLink}
                                                            onClick={() => {
                                                                openCalculater(tab, child.calculatorId);
                                                            }}
                                                        >
                                                            {child.title}
                                                        </Link>
                                                    </StyledWrapper>
                                                ))}
                                            </StyledWrapper>
                                        </Tooltip>
                                    );
                                }
                                return (
                                    <Tab
                                        key={tab.title}
                                        headline={tab.children?.length ? headTooltip : tab.title}
                                        tabKey={tab.title}
                                        {...styles.tab}
                                        onClick={() => onTabClick(tab)}
                                    >
                                        {acceptConfigButtonExists && (
                                            <JettingAppProductCard productPartNumber={productNumber} />
                                        )}

                                        <PanelsContainer
                                            tab={tab}
                                            panels={memoizedPanels}
                                            selectedCalculator={selectedCalculator}
                                            handleInputChange={handleInputChange}
                                            handleButtonClick={handleButtonClick}
                                            selectedUnit={selectedUnit}
                                            changePressureUnit={changePressureUnit}
                                            changeUnit={changeUnit}
                                            isLoadingPanels={isLoadingPanels}
                                            handleHosesChange={handleHosesChange}
                                            handleJetChange={handleJetChange}
                                            pressureUnit={selectedPressureUnit}
                                            acceptConfigButtonDisabled={acceptConfigButtonDisabled}
                                            handleResetAll={handleResetAll}
                                            onlyCalculateOnButton={onlyCalculateOnButton}
                                        />
                                    </Tab>
                                );
                            })}
                        </TabGroup>
                    )}
                </>
            </StyledWrapper>
            <TwoButtonModal
                headlineText={siteMessage(modalInfo?.title || "")}
                modalIsOpen={isConfirmationModalOpen}
                messageText={siteMessage(modalInfo?.message || "") || translate("Are you sure")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={modalInfo?.submitButton || translate("Change")}
                onCancel={handleConfirmationModalCancel}
                onSubmit={handleConfirmationModalSubmit}
                submitTestSelector="ChangeTool"
                dataTestSelector="twoButtonModal_ChangeToolModal"
            ></TwoButtonModal>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(JettingAppView)),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [],
    },
};

export default widgetModule;
